import { Form, Input, Select, Button, Typography, Row, Col, Spin, message } from 'antd';
import { useForm } from "react-hook-form";
import TabsHeader from "../../../../Components/Shared/TabsHeader";
import { CompanyStageFinepModel } from "../../../../Models/CompanyStageFinepModel";
import { yupResolver } from "@hookform/resolvers/yup";
import companyStageFinepSchema from "../../../../Schemas/companyStageFinepSchema";
import { useContext, useEffect, useState } from "react";
import { CreateCompanyStageFinep, EditCompanyStageFinep, GetCompanyStageFinep, ValidateCompanyStageFinep } from "../../../../API/CompanyStageFinepAPI";
import { useParams } from "react-router-dom";
import { useAuth } from "../../../../Hooks/useAuth";
import { CompanyFinepContext } from "../../../../Contexts/CompanyFinepContext";

const { Title, Text } = Typography;
const { Option } = Select;

const CompanyStageFinepTab = () => {
    const [loading, setLoading] = useState(false);
    const [stageId, setStageId] = useState("");
    const [preOperationalOrEarlyGrowth, setPreOperationalOrEarlyGrowth] = useState("0");
    const { changeTab, handleFillTab, handleInvalidateTab, handleValidateTab } = useContext(CompanyFinepContext);
    const [validateField, setValidateField] = useState(false);
    const user = useAuth();
    const { companyId, registerId } = useParams();
    const { register, handleSubmit, setValue, watch, getValues } = useForm<CompanyStageFinepModel>({
        resolver: yupResolver(companyStageFinepSchema),
    });

    const predecessorValue = watch('predecessor');
    
    const formatCNPJ = (value: string) => {
        if (!value) return value;
        const numericValue = value.replace(/\D/g, '');
        if (numericValue.length <= 2) return numericValue;
        if (numericValue.length <= 5) return `${numericValue.slice(0, 2)}.${numericValue.slice(2)}`;
        if (numericValue.length <= 8) return `${numericValue.slice(0, 2)}.${numericValue.slice(2, 5)}.${numericValue.slice(5)}`;
        if (numericValue.length <= 12) return `${numericValue.slice(0, 2)}.${numericValue.slice(2, 5)}.${numericValue.slice(5, 8)}/${numericValue.slice(8)}`;
        return `${numericValue.slice(0, 2)}.${numericValue.slice(2, 5)}.${numericValue.slice(5, 8)}/${numericValue.slice(8, 12)}-${numericValue.slice(12, 14)}`;
    };

    const handleCNPJChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value.replace(/\D/g, '');
        const formattedValue = formatCNPJ(rawValue);
        setValue('predecessor', formattedValue);
    };

    async function handleSubmitCompanyStage(data: CompanyStageFinepModel) {
        setLoading(true);
        data.companyFINEPId = companyId;
        data.isContentValid = validateField;
        data.userEmail = user.userEmail;
        data.preOperationalOrEarlyGrowth = preOperationalOrEarlyGrowth === "1";
        try {
            if (!stageId) {
                handleFillTab(5);
                const response = await CreateCompanyStageFinep(data);
                setStageId(response.data.id);
                message.success('Informações salvas com sucesso!');
            } else {
                data.id = stageId;
                await EditCompanyStageFinep(data);
                message.success('Informações atualizadas com sucesso!');
            }
        } catch (error) {
            message.error('Erro ao salvar informações');
        } finally {
            setLoading(false);
        }
    }

    async function getCompanyStage() {
        setLoading(true);
        if (companyId) {
            try {
                const response = await GetCompanyStageFinep(companyId);
                if (response) {
                    handleFillTab(5);
                    if (response.isContentValid) {
                        handleValidateTab(5);
                        setValidateField(true);
                    }
                    setStageId(response.id);
                    setValue('predecessor', response.predecessor);
                    setValue('preOperationalOrEarlyGrowth', response.preOperationalOrEarlyGrowth);
                    setPreOperationalOrEarlyGrowth(response.preOperationalOrEarlyGrowth ? "1" : "0");
                }
            } catch (error) {
                message.error('Erro ao carregar informações');
            } finally {
                setLoading(false);
            }
        }
    }

    async function handleValidate(isValid: boolean) {
        setLoading(true);
        setValidateField(isValid);
        try {
            await ValidateCompanyStageFinep({
                id: stageId,
                userEmail: user.userEmail,
                isContentValid: isValid
            });
            if (isValid) {
                handleValidateTab(5);
                message.success('Validação realizada com sucesso!');
            } else {
                handleInvalidateTab(5);
                message.warning('Validação removida');
            }
        } catch (error) {
            message.error('Erro ao validar informações');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        getCompanyStage();
    }, []);

    return (
        <Spin spinning={loading}>
            <TabsHeader
                canValidate={true}
                valueSelected={validateField}
                changeValidate={handleValidate}
                registerId={registerId}
                cardName=""
                tabName=""
            />
            <div className="mt-4">
                <Title level={4} style={{ color: '#000085', fontSize: '22px', fontWeight: 'normal' }}>Cadastro Pessoa Jurídica - Proponente ou Executora</Title>
                <Text style={{ color: '#777777', fontSize: '18px', fontWeight: 'normal' }}>Informações primárias da empresa</Text>
                <Form layout="vertical" onFinish={handleSubmit(handleSubmitCompanyStage)}>
                    
                    <Form.Item
                        label="A pessoa jurídica se encontra em estágio pré operacional ou crescimento inicial?"
                        className="mt-4"
                        required
                    >
                        <Select
                            placeholder="Selecione uma opção"
                            value={preOperationalOrEarlyGrowth}
                            onChange={(value) => {
                                setPreOperationalOrEarlyGrowth(value);
                                handleSubmit(handleSubmitCompanyStage)();
                            }}
                            style={{ width: '100%' }}
                        >
                            <Option value="1">Sim</Option>
                            <Option value="0">Não</Option>
                        </Select>
                    </Form.Item>
                    
                    <Form.Item
                        label="Antecessora:"
                        className="mt-4"
                        required
                        // extra="Formato: 00.000.000/0000-00"
                    >
                        <Input
                            placeholder="00.000.000/0000-00"
                            value={predecessorValue}
                            onChange={handleCNPJChange}
                            maxLength={18}
                            onBlur={handleSubmit(handleSubmitCompanyStage)}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                    
                    <Row justify="end" gutter={16} className="mt-4">
                        <Col>
                            <Button type="default" onClick={() => changeTab(4)}>Voltar</Button>
                        </Col>
                    </Row>
                </Form>
            </div>
        </Spin>
    );
};
export default CompanyStageFinepTab;